import { useCallback, useMemo } from "react";

import { useCompanyLeadSubscriptions } from "lib/hooks/api/lead_subscriptions/useCompanyLeadSubscriptions";
import { useCreateLeadSubscription } from "lib/hooks/api/lead_subscriptions/useCreateLeadSubscription";
import { useDeleteLeadSubscription } from "lib/hooks/api/lead_subscriptions/useDeleteLeadSubscription";
import { useUpdateLeadSubscription } from "lib/hooks/api/lead_subscriptions/useUpdateLeadSubscription";
import { useSavedViewsDataType } from "lib/hooks/api/saved_views/useSavedViews";
import {
  LeadSubscriptionDestinationType as CreateDestinationType,
  LeadSubscriptionFrequency,
  SavedViewDataType,
} from "lib/types/graphQLEnums";
import { SavedNoticeView } from "lib/types/savedViews";

export type IntegrationSubscriptionView = {
  id: string;
  name: string;
  description?: string | null;
  dataType: string;
};

export type IntegrationSubscription = {
  id: string;
  type: "SLACK" | "TEAMS";
  name: string;
  webhookUrl: string;
  frequency: "DAILY" | "WEEKLY";
  disabled: boolean;
  // Disabled because Slack/Teams kept refusing the webhook; saving a new URL resumes it.
  pausedByRefusals: boolean;
  views: IntegrationSubscriptionView[];
};

// Caller-facing input for create/update — webhook subscriptions only carry saved views.
export type IntegrationSubscriptionInput = {
  frequency: "DAILY" | "WEEKLY";
  name?: string;
  webhookUrl?: string;
  viewIds: string[];
};

type CompanySubscriptions = {
  slack: IntegrationSubscription[];
  teams: IntegrationSubscription[];
};

type IntegrationSubscriptions = {
  savedViews: SavedNoticeView[] | undefined;
  companySubscriptions: CompanySubscriptions | undefined;
  updateSubscription: (input: IntegrationSubscriptionInput, id: string) => Promise<void>;
  createSubscription: (
    input: IntegrationSubscriptionInput & { type: "SLACK" | "TEAMS" },
  ) => Promise<void>;
  deleteSubscription: (id: string) => Promise<void>;
  status: "error" | "loading" | "ready";
};

// Company integration (Slack/Teams) notification settings, derived directly from the GraphQL
// hooks. No context/provider needed — React Query dedupes the queries across consumers.
export function useIntegrationSubscriptions(): IntegrationSubscriptions {
  const {
    data: savedViewsData,
    isLoading: isLoadingSavedViews,
    isError: isSavedViewsError,
  } = useSavedViewsDataType(SavedViewDataType.Notice, {}, { page: 1, pageSize: 1000 });

  const { data: companyLeadSubscriptions, isLoading, isError } = useCompanyLeadSubscriptions();
  const { mutateAsync: createLeadSubscription } = useCreateLeadSubscription();
  const { mutateAsync: updateLeadSubscription } = useUpdateLeadSubscription();
  const { mutateAsync: deleteLeadSubscription } = useDeleteLeadSubscription();

  const companySubscriptions = useMemo<CompanySubscriptions | undefined>(() => {
    if (!companyLeadSubscriptions) return undefined;

    const grouped: CompanySubscriptions = { slack: [], teams: [] };
    for (const subscription of companyLeadSubscriptions) {
      const type = subscription.destinationSettings.type;
      if (type !== "SLACK" && type !== "TEAMS") continue;

      grouped[type === "SLACK" ? "slack" : "teams"].push({
        id: subscription.id,
        type,
        name: subscription.destinationSettings.name ?? "",
        webhookUrl: subscription.destinationSettings.webhookUrl ?? "",
        frequency: subscription.frequency,
        disabled: subscription.disabled,
        pausedByRefusals: subscription.pausedByRefusals,
        views: (subscription.views ?? []).map((view) => ({
          id: view.id,
          name: view.name,
          description: view.description,
          dataType: view.dataType,
        })),
      });
    }
    return grouped;
  }, [companyLeadSubscriptions]);

  const createSubscription = useCallback<IntegrationSubscriptions["createSubscription"]>(
    async (input) => {
      await createLeadSubscription({
        input: {
          frequency:
            input.frequency === "DAILY"
              ? LeadSubscriptionFrequency.Daily
              : LeadSubscriptionFrequency.Weekly,
          destinationSettings: {
            type:
              input.type === "SLACK" ? CreateDestinationType.Slack : CreateDestinationType.Teams,
            name: input.name,
            webhookUrl: input.webhookUrl,
          },
          viewIds: input.viewIds,
        },
      });
    },
    [createLeadSubscription],
  );

  const updateSubscription = useCallback<IntegrationSubscriptions["updateSubscription"]>(
    async (input, id) => {
      await updateLeadSubscription({
        input: {
          id,
          frequency:
            input.frequency === "DAILY"
              ? LeadSubscriptionFrequency.Daily
              : LeadSubscriptionFrequency.Weekly,
          // Coerce empty to undefined so the resolver preserves the stored value rather than
          // overwriting it with an empty string (the form can't submit empties anyway — validForm).
          name: input.name || undefined,
          webhookUrl: input.webhookUrl || undefined,
          viewIds: input.viewIds,
        },
      });
    },
    [updateLeadSubscription],
  );

  const deleteSubscription = useCallback<IntegrationSubscriptions["deleteSubscription"]>(
    async (id) => {
      await deleteLeadSubscription({ id });
    },
    [deleteLeadSubscription],
  );

  const status: IntegrationSubscriptions["status"] = useMemo(() => {
    if (isSavedViewsError || isError) return "error";
    if (isLoadingSavedViews || isLoading) return "loading";
    return "ready";
  }, [isSavedViewsError, isError, isLoadingSavedViews, isLoading]);

  return {
    savedViews: status === "ready" ? savedViewsData?.results : undefined,
    companySubscriptions: status === "ready" ? companySubscriptions : undefined,
    status,
    updateSubscription,
    createSubscription,
    deleteSubscription,
  };
}
